import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";

/* ─── Types ──────────────────────────────────────────────────── */
type Rsvp = {
  id: string;
  guest_name: string;
  phone: string | null;
  email: string | null;
  attendance_status: string;
  guest_count: number;
  message: string | null;
  created_at: string;
};

const STATUS_STYLES: Record<string, { badge: string; dot: string }> = {
  yes:   { badge: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  no:    { badge: "bg-red-100 text-red-600 border-red-200",             dot: "bg-red-500" },
  maybe: { badge: "bg-amber-100 text-amber-700 border-amber-200",       dot: "bg-amber-500" },
};
const STATUS_LABELS: Record<string, string> = {
  yes:   "Attending",
  no:    "Not Attending",
  maybe: "Maybe",
};
const STATUS_ICONS: Record<string, string> = {
  yes:   "ri-checkbox-circle-fill",
  no:    "ri-close-circle-line",
  maybe: "ri-question-line",
};

/* ─── CSV Export ─────────────────────────────────────────────── */
function exportCSV(rsvps: Rsvp[], title: string) {
  const headers = ["Name", "Phone", "Email", "Attendance", "Guests", "Message", "Date"];
  const rows = rsvps.map((r) => [
    `"${r.guest_name}"`,
    r.phone ?? "",
    r.email ?? "",
    STATUS_LABELS[r.attendance_status] ?? r.attendance_status,
    String(r.guest_count),
    `"${(r.message ?? "").replace(/"/g, "'")}"`,
    new Date(r.created_at).toLocaleDateString("en-US"),
  ]);
  const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `rsvp-${title.replace(/\s+/g, "-").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ─── Props ──────────────────────────────────────────────────── */
type InvForm = Invitation & { rsvp_message?: string | null };

interface Props {
  form: InvForm;
  tenantId: string;
  onChange: <K extends keyof InvForm>(key: K, val: InvForm[K]) => void;
}

/* ─── Component ──────────────────────────────────────────────── */
export default function RsvpTab({ form, tenantId, onChange }: Props) {
  const [rsvps, setRsvps]         = useState<Rsvp[]>([]);
  const [loading, setLoading]     = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter]       = useState<"all" | "yes" | "no" | "maybe">("all");
  const [busyId, setBusyId]       = useState<string | null>(null);
  const [search, setSearch]       = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fetchRsvps = useCallback(async (silent = false) => {
    if (!form.id) return;
    if (silent) setRefreshing(true); else setLoading(true);
    const { data } = await supabase
      .from("invitation_rsvps")
      .select("*")
      .eq("invitation_id", form.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });
    setRsvps((data as Rsvp[]) ?? []);
    if (silent) setRefreshing(false); else setLoading(false);
  }, [form.id, tenantId]);

  useEffect(() => { fetchRsvps(); }, [fetchRsvps]);

  const handleDelete = async (rsvp: Rsvp) => {
    const result = await Swal.fire({
      title: "Remove RSVP?",
      html: `Remove <strong>${rsvp.guest_name}</strong>'s response? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed) return;
    setBusyId(rsvp.id);
    const { error } = await supabase
      .from("invitation_rsvps")
      .delete()
      .eq("id", rsvp.id)
      .eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error" });
    } else {
      setRsvps((prev) => prev.filter((r) => r.id !== rsvp.id));
    }
    setBusyId(null);
  };

  /* ── Computed stats ── */
  const yesRsvps    = rsvps.filter((r) => r.attendance_status === "yes");
  const noRsvps     = rsvps.filter((r) => r.attendance_status === "no");
  const maybeRsvps  = rsvps.filter((r) => r.attendance_status === "maybe");
  const totalGuests = yesRsvps.reduce((s, r) => s + r.guest_count, 0);

  /* ── Filtered + searched list ── */
  const byStatus  = filter === "all" ? rsvps : rsvps.filter((r) => r.attendance_status === filter);
  const filtered  = search.trim()
    ? byStatus.filter((r) =>
        r.guest_name.toLowerCase().includes(search.toLowerCase()) ||
        (r.phone ?? "").includes(search) ||
        (r.email ?? "").toLowerCase().includes(search.toLowerCase()),
      )
    : byStatus;

  /* ── Attendance bar widths ── */
  const total = rsvps.length || 1;
  const yesPct   = Math.round((yesRsvps.length / total) * 100);
  const noPct    = Math.round((noRsvps.length / total) * 100);
  const maybePct = Math.round((maybeRsvps.length / total) * 100);

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">RSVP Settings & Responses</h3>
        <p className="text-slate-400 text-xs">Configure the RSVP form and manage guest responses.</p>
      </div>

      {/* ── Toggle + message ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-4 flex items-center justify-between">
          <div>
            <p className="text-slate-700 font-medium text-sm">Enable RSVP Form</p>
            <p className="text-slate-400 text-xs mt-0.5">Allow guests to confirm attendance on the invitation page</p>
          </div>
          <div
            onClick={() => onChange("rsvp_enabled", !form.rsvp_enabled)}
            className={`w-11 h-6 rounded-full transition-colors relative cursor-pointer shrink-0 ${
              form.rsvp_enabled ? "bg-indigo-500" : "bg-slate-200"
            }`}
          >
            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.rsvp_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
        </div>

        {form.rsvp_enabled && (
          <div className="px-4 pb-4 border-t border-slate-100 pt-4">
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Intro Message <span className="text-slate-300 normal-case font-normal">(shown above the form)</span>
            </label>
            <textarea
              value={form.rsvp_message ?? ""}
              onChange={(e) => onChange("rsvp_message" as keyof InvForm, (e.target.value || null) as InvForm[keyof InvForm])}
              rows={2}
              maxLength={500}
              placeholder="Kindly confirm your attendance by [date]. We look forward to celebrating with you!"
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all resize-none"
            />
            <p className="text-slate-300 text-xs mt-1 text-right">{(form.rsvp_message ?? "").length}/500</p>
          </div>
        )}
      </div>

      {/* ── Stats (only if invitation exists) ── */}
      {form.id ? (
        <>
          {/* Stats cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Attending",    value: yesRsvps.length,   sub: `${totalGuests} total guests`, color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100", icon: "ri-checkbox-circle-fill", iconColor: "text-emerald-500" },
              { label: "Not Coming",   value: noRsvps.length,    sub: "",                            color: "text-red-500",     bg: "bg-red-50 border-red-100",         icon: "ri-close-circle-line",    iconColor: "text-red-400" },
              { label: "Maybe",        value: maybeRsvps.length, sub: "",                            color: "text-amber-600",   bg: "bg-amber-50 border-amber-100",     icon: "ri-question-line",        iconColor: "text-amber-500" },
              { label: "Total RSVPs",  value: rsvps.length,      sub: "",                            color: "text-slate-700",   bg: "bg-slate-50 border-slate-200",     icon: "ri-group-line",           iconColor: "text-slate-400" },
            ].map(({ label, value, sub, color, bg, icon, iconColor }) => (
              <div key={label} className={`rounded-xl border p-4 ${bg}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`w-6 h-6 flex items-center justify-center ${iconColor}`}>
                    <i className={`${icon} text-base`}></i>
                  </span>
                </div>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-slate-500 text-xs mt-0.5">{label}</p>
                {sub && <p className="text-slate-400 text-[10px] mt-0.5">{sub}</p>}
              </div>
            ))}
          </div>

          {/* Attendance bar */}
          {rsvps.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-3">Response Breakdown</p>
              <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
                {yesPct > 0   && <div className="bg-emerald-400 transition-all" style={{ width: `${yesPct}%` }} title={`Attending: ${yesPct}%`} />}
                {noPct > 0    && <div className="bg-red-400 transition-all"     style={{ width: `${noPct}%` }}  title={`Not coming: ${noPct}%`} />}
                {maybePct > 0 && <div className="bg-amber-400 transition-all"   style={{ width: `${maybePct}%` }} title={`Maybe: ${maybePct}%`} />}
              </div>
              <div className="flex items-center gap-4 mt-2.5">
                {[
                  { label: "Attending",   pct: yesPct,   color: "bg-emerald-400" },
                  { label: "Not coming",  pct: noPct,    color: "bg-red-400" },
                  { label: "Maybe",       pct: maybePct, color: "bg-amber-400" },
                ].map(({ label, pct, color }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${color}`} />
                    <span className="text-slate-500 text-xs">{label} {pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Filter tabs */}
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                {(["all", "yes", "no", "maybe"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                      filter === f ? "bg-white text-slate-700 shadow-sm" : "text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    {f === "all" ? `All (${rsvps.length})` : `${STATUS_LABELS[f]} (${rsvps.filter((r) => r.attendance_status === f).length})`}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 flex items-center justify-center text-slate-400">
                  <i className="ri-search-line text-xs"></i>
                </span>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search guests…"
                  className="pl-7 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-300 text-slate-600 placeholder-slate-300 w-36"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Refresh */}
              <button
                onClick={() => fetchRsvps(true)}
                disabled={refreshing}
                className="flex items-center gap-1.5 border border-slate-200 text-slate-400 hover:text-slate-600 text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <span className={`w-3.5 h-3.5 flex items-center justify-center ${refreshing ? "animate-spin" : ""}`}>
                  <i className="ri-refresh-line"></i>
                </span>
                Refresh
              </button>

              {/* Export CSV */}
              {rsvps.length > 0 && (
                <button
                  onClick={() => exportCSV(rsvps, form.title)}
                  className="flex items-center gap-1.5 border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 text-xs px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-download-2-line"></i>
                  </span>
                  Export CSV
                </button>
              )}
            </div>
          </div>

          {/* RSVP list */}
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 text-slate-300 mx-auto mb-3">
                <i className="ri-group-line text-2xl"></i>
              </div>
              <p className="text-slate-500 font-medium text-sm">
                {rsvps.length === 0 ? "No RSVPs yet" : "No results found"}
              </p>
              <p className="text-slate-400 text-xs mt-1">
                {rsvps.length === 0
                  ? "Responses will appear here once guests submit the form."
                  : "Try adjusting your filter or search term."}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                <div className="col-span-4">Guest</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2 text-center">Guests</div>
                <div className="col-span-3">Contact</div>
                <div className="col-span-1"></div>
              </div>

              <div className="divide-y divide-slate-50">
                {filtered.map((rsvp) => {
                  const st = STATUS_STYLES[rsvp.attendance_status] ?? STATUS_STYLES.maybe;
                  const isExpanded = expandedId === rsvp.id;
                  return (
                    <div key={rsvp.id}>
                      <div
                        className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-slate-50/50 transition-colors cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : rsvp.id)}
                      >
                        {/* Guest name + date */}
                        <div className="col-span-4 flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold shrink-0">
                            {rsvp.guest_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-slate-700 text-sm font-medium truncate">{rsvp.guest_name}</p>
                            <p className="text-slate-400 text-[10px]">
                              {new Date(rsvp.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                            </p>
                          </div>
                        </div>

                        {/* Status badge */}
                        <div className="col-span-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold border ${st.badge}`}>
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className={STATUS_ICONS[rsvp.attendance_status] ?? "ri-question-line"}></i>
                            </span>
                            {STATUS_LABELS[rsvp.attendance_status] ?? rsvp.attendance_status}
                          </span>
                        </div>

                        {/* Guest count */}
                        <div className="col-span-2 text-center">
                          {rsvp.attendance_status === "yes" ? (
                            <span className="inline-flex items-center gap-1 text-xs text-slate-600 font-semibold">
                              <span className="w-3.5 h-3.5 flex items-center justify-center text-slate-400">
                                <i className="ri-group-line text-xs"></i>
                              </span>
                              {rsvp.guest_count}
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </div>

                        {/* Contact */}
                        <div className="col-span-3 min-w-0">
                          <div className="space-y-0.5">
                            {rsvp.phone && (
                              <p className="text-slate-500 text-xs truncate flex items-center gap-1">
                                <span className="w-3 h-3 flex items-center justify-center text-slate-400 shrink-0">
                                  <i className="ri-phone-line text-[10px]"></i>
                                </span>
                                {rsvp.phone}
                              </p>
                            )}
                            {rsvp.email && (
                              <p className="text-slate-500 text-xs truncate flex items-center gap-1">
                                <span className="w-3 h-3 flex items-center justify-center text-slate-400 shrink-0">
                                  <i className="ri-mail-line text-[10px]"></i>
                                </span>
                                {rsvp.email}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="col-span-1 flex items-center justify-end gap-1">
                          <span className="w-5 h-5 flex items-center justify-center text-slate-300">
                            <i className={`${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`}></i>
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(rsvp); }}
                            disabled={busyId === rsvp.id}
                            className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-red-400 hover:bg-red-50 rounded-md transition-all cursor-pointer disabled:opacity-40"
                          >
                            {busyId === rsvp.id
                              ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" />
                              : <i className="ri-delete-bin-line text-xs"></i>
                            }
                          </button>
                        </div>
                      </div>

                      {/* Expanded row — message */}
                      {isExpanded && (
                        <div className="px-4 pb-3 pt-0 bg-slate-50/50 border-t border-slate-100">
                          <div className="pl-9">
                            {rsvp.message ? (
                              <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                                <p className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Message</p>
                                <p className="text-slate-600 text-sm italic">&ldquo;{rsvp.message}&rdquo;</p>
                              </div>
                            ) : (
                              <p className="text-slate-300 text-xs italic">No message left.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer summary */}
              {filtered.length > 0 && (
                <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-slate-400 text-xs">{filtered.length} response{filtered.length !== 1 ? "s" : ""}</p>
                  {filter === "yes" && (
                    <p className="text-emerald-600 text-xs font-semibold">
                      {totalGuests} total guests confirmed
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 flex items-start gap-3">
          <span className="w-5 h-5 flex items-center justify-center text-amber-500 shrink-0 mt-0.5">
            <i className="ri-alert-line"></i>
          </span>
          <div>
            <p className="text-amber-700 font-medium text-sm">Save the invitation first</p>
            <p className="text-amber-600 text-xs mt-0.5">RSVP responses will appear here once the invitation is saved and published.</p>
          </div>
        </div>
      )}
    </div>
  );
}
